import { lazy } from 'react';

/**
 * Code-split route components live here rather than in router.tsx so that file
 * exports only the router — which is what keeps Fast Refresh working while
 * editing routes.
 *
 * Only the feed and the app shell are eager. Everything else is a route most
 * sessions never open, and the spec's mobile Lighthouse budget is a budget on
 * what the *first* screen costs — 330 KB of JavaScript before the first post
 * appears is what took mobile performance below 90.
 *
 * The styleguide is development tooling and has no business in a reader's
 * bundle at all. The map is the heaviest single route: its world topology and
 * projection maths are ~215 KB that most sessions never open.
 */
export const LazyStyleguidePage = lazy(() =>
  import('@/pages/dev/StyleguidePage').then((module) => ({ default: module.StyleguidePage })),
);

export const LazyMapPage = lazy(() =>
  import('@/pages/MapPage').then((module) => ({ default: module.MapPage })),
);

export const LazyPostDetailPage = lazy(() =>
  import('@/pages/PostDetailPage').then((module) => ({ default: module.PostDetailPage })),
);

export const LazyProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((module) => ({ default: module.ProfilePage })),
);

export const LazySettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((module) => ({ default: module.SettingsPage })),
);

export const LazySearchPage = lazy(() =>
  import('@/pages/SearchPage').then((module) => ({ default: module.SearchPage })),
);

export const LazyNotificationsPage = lazy(() =>
  import('@/pages/NotificationsPage').then((module) => ({ default: module.NotificationsPage })),
);

export const LazyBookmarksPage = lazy(() =>
  import('@/pages/BookmarksPage').then((module) => ({ default: module.BookmarksPage })),
);

export const LazyHallOfFamePage = lazy(() =>
  import('@/pages/HallOfFamePage').then((module) => ({ default: module.HallOfFamePage })),
);

export const LazyTransparencyPage = lazy(() =>
  import('@/pages/TransparencyPage').then((module) => ({ default: module.TransparencyPage })),
);

export const LazyBusinessProfileRedirect = lazy(() =>
  import('@/pages/BusinessProfileRedirect').then((module) => ({
    default: module.BusinessProfileRedirect,
  })),
);

export const LazyBusinessInboxPage = lazy(() =>
  import('@/pages/BusinessInboxPage').then((module) => ({ default: module.BusinessInboxPage })),
);

export const LazyBusinessDashboardPage = lazy(() =>
  import('@/pages/BusinessDashboardPage').then((module) => ({
    default: module.BusinessDashboardPage,
  })),
);

export const LazyModerationPage = lazy(() =>
  import('@/pages/ModerationPage').then((module) => ({ default: module.ModerationPage })),
);

/**
 * Sign-in is split too, for a reason worth naming: it is the only eager route
 * that imports react-hook-form, and that alone put the whole form stack —
 * 134 KB of source — in front of the first post for every anonymous reader.
 */
export const LazyLoginPage = lazy(() =>
  import('@/pages/auth/LoginPage').then((module) => ({ default: module.LoginPage })),
);

export const LazyRegisterPage = lazy(() =>
  import('@/pages/auth/RegisterPage').then((module) => ({ default: module.RegisterPage })),
);

export const LazyForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then((module) => ({
    default: module.ForgotPasswordPage,
  })),
);

export const LazyResetPasswordPage = lazy(() =>
  import('@/pages/auth/ResetPasswordPage').then((module) => ({
    default: module.ResetPasswordPage,
  })),
);

export const LazyVerifyEmailPage = lazy(() =>
  import('@/pages/auth/VerifyEmailPage').then((module) => ({ default: module.VerifyEmailPage })),
);

/**
 * The composer, split as spec §11 asks.
 *
 * It pulls in the form stack, the media upload pipeline and the bad-post
 * picker, none of which a reader needs until they decide to write something.
 */
export const LazyComposerDialog = lazy(() =>
  import('@/features/composer/ComposerDialog').then((module) => ({
    default: module.ComposerDialog,
  })),
);

/**
 * The business profile. Behind the same flag as the rest of the business work,
 * but split regardless: it is a route most sessions never open.
 */
export const LazyCompleteRegistrationPage = lazy(() =>
  import('@/pages/auth/CompleteRegistrationPage').then((module) => ({
    default: module.CompleteRegistrationPage,
  })),
);

export const LazyAcceptInvitePage = lazy(() =>
  import('@/pages/AcceptInvitePage').then((module) => ({ default: module.AcceptInvitePage })),
);

export const LazyForBusinessPage = lazy(() =>
  import('@/pages/ForBusinessPage').then((module) => ({ default: module.ForBusinessPage })),
);
